import React, { useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import CloseIcon from 'icons/close.svg';
import CreateNewFolderIcon from 'icons/create-new-folder.svg';
import FolderOffIcon from 'icons/folder-off.svg';
import FolderIcon from 'icons/folder.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { useCreateProjectsBulkFolderAssignmentMutation } from 'components/hooks/projectsBulkFolderAssignmentMutations';
import useDebounce from 'components/hooks/useDebounce';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import FolderSelect from 'components/folders/FolderSelect';
import { useProjectRelocationModalContext } from 'components/projectsDashboard/contexts/ProjectRelocationModalContext';

import {
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';
import { pluralizeProjectsName } from 'components/helpers/resources/projects';
import { resourcesAsResourceMap } from 'components/helpers/serialisableResources';

export default function MoveToFolderModal({
  contextualFolderId,
  onClose,
  onGroupIntoFolderClick,
  onSuccess,
  selectedProjectIds,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const {
    creationBlockingExistingFolder,
    handleCurrentFolderInputChange,
    handleDestinationFolderSelect,
    handleReset,
    selectedDestinationFolderId,
  } = useProjectRelocationModalContext();

  const [isRemovingFromFolder, setIsRemovingFromFolder] = useState(false);
  const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false);
  const [searchParam, setSearchParam] = useState('');

  const [debouncedSearchParam, _resetDebouncedSearchParam] = useDebounce(
    searchParam,
    250,
  );

  const [loadMoreFoldersRef, isLoadMoreFoldersInView] = useInView();

  const createProjectsBulkFolderAssignmentMutation =
    useCreateProjectsBulkFolderAssignmentMutation();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.MoveToFolderModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const filter =
    contextualFolderId ? { ids: { nin: [contextualFolderId] } } : {};

  const {
    data: domainFolders,
    fetchNextPage: fetchDomainFoldersNextPage,
    hasNextPage: hasDomainFoldersNextPage,
    isFetching: isFetchingDomainFolders,
  } = useInfiniteQuery({
    enabled: isSelectMenuOpen || !!selectedDestinationFolderId,
    getNextPageParam,
    keepPreviousData: true,
    queryKey: ['folders', { filter, search: debouncedSearchParam }],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.post('/folder_searches', {
        folder_search: {
          filter,
          page: pageParam,
          search: debouncedSearchParam,
        },
      });

      return response.data;
    },
    select: (data) => formatInfiniteData({ data }),
  });

  const handleSubmit = () => {
    createProjectsBulkFolderAssignmentMutation.mutate(
      {
        bulkFolderAssignmentParams: {
          folder_id: isRemovingFromFolder ? null : selectedDestinationFolderId,
          project_ids: selectedProjectIds,
        },
      },
      {
        onSuccess: (data, variables) => {
          handleReset();

          const resourceMap = resourcesAsResourceMap({
            resources: data.included,
            resourceTypes: ['folder'],
          });

          const folder =
            resourceMap.folder[data.data.relationships.folder.data?.id];

          onSuccess({
            folder,
            selectedProjectIdsCount:
              variables.bulkFolderAssignmentParams.project_ids.length,
          });
        },
        onError: (_data, variables) => {
          breadBoard.addInedibleToast({
            fullMessage: i18nT('handleSubmit.error', {
              interpolation: { escapeValue: false },
              ramsName: pluralizeProjectsName({
                count: variables.bulkFolderAssignmentParams.project_ids.length,
                currentActor,
              }),
            }),
          });
        },
      },
    );
  };

  const isCreationBlockingExistingFolderTheDestinationFolder =
    creationBlockingExistingFolder &&
    creationBlockingExistingFolder.id === selectedDestinationFolderId;

  const selectedFolder = (function selectedFolder() {
    if (!isRemovingFromFolder) {
      if (selectedDestinationFolderId) {
        return isCreationBlockingExistingFolderTheDestinationFolder ?
            creationBlockingExistingFolder
          : domainFolders?.data.find(
              (folder) => folder.id === selectedDestinationFolderId,
            );
      }
    }
    return null;
  })();

  const ramsName = pluralizeProjectsName({
    count: selectedProjectIds.length,
    currentActor,
  });

  const shouldLoadMoreFolders =
    !isFetchingDomainFolders &&
    domainFolders?.data.length > 0 &&
    hasDomainFoldersNextPage &&
    isLoadMoreFoldersInView;

  if (shouldLoadMoreFolders) fetchDomainFoldersNextPage();

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={() => {
              handleReset();
              onClose();
            }}
            size='md'
          >
            {i18nTShared('cancel')}
          </TextButton>
          <FilledButton
            color='mint'
            disabled={
              !(selectedFolder || isRemovingFromFolder) ||
              createProjectsBulkFolderAssignmentMutation.isLoading
            }
            onClick={handleSubmit}
          >
            {i18nT('button', {
              interpolation: { escapeValue: false },
              ramsName,
            })}
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='flexible'
      onClose={() => {
        handleReset();
        onClose();
      }}
    >
      <div className='tw-flex tw-flex-col tw-gap-6 tw-p-6'>
        <p className='tw-mb-0 tw-text-xl tw-font-semibold tw-tracking-tight'>
          {i18nT(
            `title.${contextualFolderId ? 'inFolderContext' : 'notInFolderContext'}`,
            {
              count: selectedProjectIds.length,
              interpolation: { escapeValue: false },
              ramsName,
            },
          )}
        </p>
        {selectedFolder ?
          <div className='tw-flex tw-items-center tw-gap-1'>
            <div className='tw-flex tw-h-10 tw-w-full tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-lg tw-bg-blue-025 tw-px-3'>
              <FolderIcon
                className='tw-flex-none [&_path]:tw-fill-grey-700'
                height={20}
                width={20}
              />
              <p className='tw-mb-0 tw-min-w-0 tw-flex-auto tw-truncate tw-text-m tw-font-normal tw-text-grey-900'>
                {selectedFolder.attributes.name}
              </p>
            </div>
            <button
              className='tw-h-5 tw-w-5 tw-border-0 tw-bg-white tw-p-0'
              onClick={() => {
                handleDestinationFolderSelect(contextualFolderId);
                setSearchParam('');
              }}
            >
              <CloseIcon
                className='[&_path]:tw-fill-grey-500'
                height={20}
                width={20}
              />
              <span className='tw-sr-only'>{i18nTShared('clear')}</span>
            </button>
          </div>
        : isRemovingFromFolder ?
          <div className='tw-flex tw-items-center tw-gap-1'>
            <div className='tw-flex tw-h-10 tw-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-bg-amber-025 tw-px-3'>
              <FolderOffIcon
                className='[&_path]:tw-fill-grey-700'
                height={20}
                width={20}
              />
              <p className='tw-mb-0 tw-text-m tw-font-normal tw-text-grey-900'>
                {i18nT('removeFromFolder')}
              </p>
            </div>
            <button
              className='tw-h-5 tw-w-5 tw-border-0 tw-bg-white tw-p-0'
              onClick={() => setIsRemovingFromFolder(false)}
            >
              <CloseIcon
                className='[&_path]:tw-fill-grey-500'
                height={20}
                width={20}
              />
              <span className='tw-sr-only'>{i18nTShared('clear')}</span>
            </button>
          </div>
        : <div className='tw-flex tw-w-full tw-items-center tw-gap-2'>
            <FolderSelect
              createLabel={(input) => (
                <div className='tw-flex tw-items-center tw-gap-1 tw-font-medium tw-text-blue-500'>
                  <CreateNewFolderIcon
                    className='[&_path]:tw-fill-blue-500'
                    height={20}
                    width={20}
                  />
                  {i18nT('select.createLabel', {
                    input,
                    interpolation: { escapeValue: false },
                  })}
                </div>
              )}
              folders={domainFolders?.data || []}
              isCreatable={true}
              isLoading={isFetchingDomainFolders}
              loadMoreRef={loadMoreFoldersRef}
              noOptionsMessage={
                <p className='tw-mb-0 tw-text-left tw-text-grey-400'>
                  {debouncedSearchParam ?
                    i18nT('select.noOptionsMessage.noResults', {
                      interpolation: { escapeValue: false },
                      searchParam: debouncedSearchParam,
                    })
                  : i18nT('select.noOptionsMessage.noFolders')}
                </p>
              }
              onCreateOption={(input) => {
                handleCurrentFolderInputChange({
                  target: { name: 'name', value: input },
                });

                onGroupIntoFolderClick();
              }}
              onInputChange={(value, actionMeta) => {
                // When the action is set-value (selecting an option), the value will be '' and this will trigger a folders fetch with an empty search param.
                // The selected folder may no longer be found via the query (depending on pagination).
                if (actionMeta.action !== 'set-value') setSearchParam(value);
              }}
              onMenuClose={() => setIsSelectMenuOpen(false)}
              onMenuOpen={() => setIsSelectMenuOpen(true)}
              onSelect={handleDestinationFolderSelect}
            />
            {!!contextualFolderId && (
              <>
                <p className='tw-mb-0 tw-text-m tw-font-normal tw-text-grey-700'>
                  {i18nTShared('conjunctions.or')}
                </p>
                <button
                  className='tw-flex-shrink-0 tw-border-0 tw-bg-white tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
                  onClick={() => setIsRemovingFromFolder(true)}
                  type='button'
                >
                  {i18nT('removeFromFolder')}
                </button>
              </>
            )}
          </div>
        }
      </div>
    </ModalBox>
  );
}

MoveToFolderModal.propTypes = {
  contextualFolderId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onGroupIntoFolderClick: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  selectedProjectIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};
